import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useClient } from '../../../contexts/ClientContext';
import { useProject } from '../../../contexts/ProjectContext';
import { useFilter } from '../../../contexts/FilterContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { PERMISSIONS } from '../../../utils/permissions';
import {
  getGuardrailsEnvironments,
  getGuardrailsViolations,
} from '../../../services/cloudopsApi';
import { FiCheckCircle as CheckCircle2 } from 'react-icons/fi';
import { Card, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import ViolationCard from '../components/ViolationCard';

interface ViolationEnvironment {
  id: string;
  name?: string;
  aws_account_id?: string;
}

interface Violation {
  id: string;
  severity?: string;
  category?: string;
  [key: string]: unknown;
}

interface PaginationMeta {
  total: number;
  totalPages: number;
  page: number;
  limit: number;
}

const ViolationsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { selectedClientId, isLoadingClients } = useClient();
  const { selectedProjectId } = useProject();
  const { hasPermission } = usePermissions();
  const { isAWSUser } = useModuleAccess();
  const { filterType, selectedId: filterId } = useFilter();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [violations, setViolations] = useState<Violation[]>([]);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || 'all');
  const [severityFilter, setSeverityFilter] = useState<string>(searchParams.get('severity') || 'all');
  const [categoryFilter, setCategoryFilter] = useState<string>(searchParams.get('category') || 'all');

  // Derive environment filter from global filter
  const environmentFilter = filterType === 'environment' && filterId ? filterId : 'all';

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(Number.parseInt(searchParams.get('page') || '1'));
  const [limit] = useState<number>(15);
  const [paginationMeta, setPaginationMeta] = useState<PaginationMeta | null>(null);

  useEffect(() => {
    // Reset to page 1 when filters change (except page itself)
    setCurrentPage(1);
  }, [statusFilter, severityFilter, categoryFilter, environmentFilter]);

  useEffect(() => {
    // Update URL params when filters or page change
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (severityFilter !== 'all') params.set('severity', severityFilter);
    if (categoryFilter !== 'all') params.set('category', categoryFilter);
    if (currentPage > 1) params.set('page', String(currentPage));
    setSearchParams(params, { replace: true });
  }, [statusFilter, severityFilter, categoryFilter, currentPage, setSearchParams]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Avoid calling /guardrails/environments without client_id for partner/aws users.
      const needsClientId = isAWSUser() ||
        hasPermission(PERMISSIONS.CLIENTS_VIEW) ||
        hasPermission(PERMISSIONS.CLIENTS_MANAGE) ||
        hasPermission(PERMISSIONS.PARTNERS_VIEW) ||
        hasPermission(PERMISSIONS.PARTNERS_MANAGE);
      if (needsClientId && !selectedClientId) {
        if (isLoadingClients) return;
        setError('Please select a client to view violations.');
        return;
      }
      const clientId = selectedClientId || user?.client_id || undefined;
      const environmentsData = await getGuardrailsEnvironments(clientId);

      // Load violations for all environments or selected environment
      if (environmentFilter === 'all') {
        // When 'all' is selected, fetch page 1 from each environment
        const environmentIds = (environmentsData || []).map((e: ViolationEnvironment) => e.id);
        const violationsPromises = environmentIds.map((environmentId: string) =>
          getGuardrailsViolations(environmentId, statusFilter === 'all' ? undefined : statusFilter, 1, limit)
            .catch(() => ({ data: [], meta: null }))
        );

        const violationsResults = await Promise.all(violationsPromises);
        const allViolations = violationsResults.flatMap(result => result.data || result);
        setViolations(allViolations);
        setPaginationMeta(null); // No pagination meta when viewing all environments
      } else {
        // When a specific environment is selected, use pagination
        const response = await getGuardrailsViolations(
          environmentFilter,
          statusFilter === 'all' ? undefined : statusFilter,
          currentPage,
          limit
        );
        
        // Handle both old format (array) and new format (object with data/meta)
        if (Array.isArray(response)) {
          setViolations(response);
          setPaginationMeta(null);
        } else {
          setViolations(response.data || []);
          setPaginationMeta(response.meta || null);
        }
      }
    } catch (err: unknown) {
      console.error('Error loading violations:', err);
      setError(err instanceof Error ? err.message : 'Failed to load violations');
    } finally {
      setLoading(false);
    }
  }, [selectedClientId, selectedProjectId, isLoadingClients, user?.client_id, statusFilter, environmentFilter, currentPage, limit, hasPermission, isAWSUser, filterType, filterId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredViolations = violations.filter(v => {
    if (severityFilter !== 'all' && v.severity?.toLowerCase() !== severityFilter.toLowerCase()) {
      return false;
    }
    if (categoryFilter !== 'all' && v.category?.toLowerCase() !== categoryFilter.toLowerCase()) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Violations
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Monitor and manage compliance violations
        </p>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
          <Button
            onClick={loadData}
            variant="ghost"
            size="sm"
            className="mt-2"
          >
            Try again
          </Button>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'open', label: 'Open' },
                { value: 'acknowledged', label: 'Acknowledged' },
                { value: 'resolved', label: 'Resolved' },
                { value: 'false_positive', label: 'False Positive' },
              ]}
            />
            <Select
              label="Severity"
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Severities' },
                { value: 'critical', label: 'Critical' },
                { value: 'high', label: 'High' },
                { value: 'medium', label: 'Medium' },
                { value: 'low', label: 'Low' },
              ]}
            />
            <Select
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Categories' },
                { value: 'security', label: 'Security' },
                { value: 'cost', label: 'Cost Management' },
                { value: 'governance', label: 'Governance' },
                { value: 'reliability', label: 'Reliability' },
              ]}
            />
          </div>
        </CardContent>
      </Card>

      {/* Violations List */}
      {filteredViolations.length > 0 ? (
        <>
          <div className="space-y-4">
            {filteredViolations.map((violation) => (
              <ViolationCard
                key={violation.id}
                violation={violation}
                onResolve={loadData}
              />
            ))}
          </div>
          
          {/* Pagination */}
          {(paginationMeta?.totalPages ?? 0) > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <Button
                variant="secondary"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-gray-700 dark:text-gray-300 px-4">
                Page {currentPage} of {paginationMeta!.totalPages}
                {paginationMeta!.total && (
                  <span className="text-gray-500 dark:text-gray-400 text-sm ml-2">
                    ({paginationMeta!.total} total)
                  </span>
                )}
              </span>
              <Button
                variant="secondary"
                onClick={() => setCurrentPage(p => Math.min(paginationMeta!.totalPages, p + 1))}
                disabled={currentPage === paginationMeta!.totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No violations found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {violations.length === 0
                ? 'No violations match your current filters'
                : 'All violations have been resolved'}
            </p>
          </CardContent>
        </Card>
      )}
      
      {/* Info message when viewing all environments */}
      {environmentFilter === 'all' && violations.length > 0 && (
        <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-blue-800 dark:text-blue-200 text-sm">
            Showing first page of violations from all environments. Use the global filter to select a specific environment for pagination.
          </p>
        </div>
      )}
    </div>
  );
};

export default ViolationsPage;
